import { queryClient } from '@/providers/ReactQueryProvider';
import React, { createContext, useContext, useReducer } from 'react';

interface AuthContextType {
  isAuth: boolean;
  login: (res: any) => void;
  logout: (err?: any) => void;
}
const AuthContext = createContext<AuthContextType | undefined>(undefined);
interface AuthProviderProps {
  children: React.ReactNode;
}
enum Type {
  LOGIN = 'LOGIN',
  LOGOUT = 'LOGOUT',
}
interface ActionType {
  type: Type.LOGIN | Type.LOGOUT;
  payload?: any;
}

const authHandler = (state: boolean, action: ActionType): boolean => {
  switch (action.type) {
    case Type.LOGIN: {
      const storageItem = {
        access_token: action.payload.data.access_token,
        refresh_token: action.payload.data.refresh_token,
        token_expiry: `${new Date().getTime() + action.payload.data.expires_in * 1000}`,
      };
      Object.entries(storageItem).forEach(([k, v]) =>
        localStorage.setItem(k, v),
      );
      return true;
    }
    case Type.LOGOUT: {
      ['access_token', 'refresh_token', 'token_expiry'].forEach((i) => {
        localStorage.removeItem(i);
      });
      localStorage.clear();
      queryClient.removeQueries();
      return false;
    }
    default:
      return state;
  }
};

const tokenValidation = () => {
  const access_token = localStorage.getItem('access_token');
  const token_expiry = localStorage.getItem('token_expiry');
  const tokenExpiry = token_expiry ? parseInt(token_expiry, 10) : 0;
  return !!(access_token && token_expiry && new Date().getTime() < tokenExpiry);
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuth, dispatch] = useReducer(authHandler, tokenValidation());
  const login = (res: any) => {
    Promise.resolve()
      .then(() => {
        dispatch({ type: Type.LOGIN, payload: res });
      })
      .then(() => {});
  };
  const logout = (err?: any) => {
    Promise.resolve()
      .then(() => {
        dispatch({ type: Type.LOGOUT, payload: err });
      })
      .then(() => {});
  };
  const contextValue: AuthContextType = {
    isAuth,
    login,
    logout,
  };
  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
